import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "../hooks/use-mobile";
import { useLanguage } from "../i18n/LanguageContext";
import {
  ClinicScheduleStatusDto,
  DoctorDto,
  SpecialtiesClient,
} from "../web-api-client";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { DoctorCard } from "../components/booking/doctor/DoctorCard";
import { scrollToTop } from "../utils/scrollToTop";
import HorizontalCard from "../components/booking/room/HorizontalCard";
import ClinicDetailModal from "../components/booking/ClinicDetail";
import NonScheduleDialog from "../components/NoScheduleDialog";
import { searchDoctors, searchClinics } from "../utils/filter";
import { useDebounce } from "../hooks/useDebounce";
import { useDoctors } from "../hooks/useDoctors";
import ErrorRetry from "../components/ui/ErrorRetry";
import LoadingIndicator from "../components/LoadingIndicator";
import { appConfig } from "../appconfig";

export default function BookingSearch() {
  type ModalType = "submit" | "detail";

  const location = useLocation();
  const type = location.state?.type;
  const initQuery = location.state?.query;

  const [isMobile] = useIsMobile();
  const [query, setQuery] = useState(initQuery ?? "");
  const [activeTab, setActiveTab] = useState(type ?? "doctor");
  const navigate = useNavigate();
  const { t } = useLanguage();

  const searchRef = useRef<HTMLInputElement>(null);

  const [clinics, setClinics] = useState<ClinicScheduleStatusDto[]>([]);
  const [selectedClinic, setSelectedClinic] =
    useState<ClinicScheduleStatusDto | null>(null);
  const [modalType, setModalType] = useState<string | "">("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isEmptySlot, setIsEmptySlot] = useState(false);

  const [emptyDataMessage, setEmptyDataMessage] = useState("");

  const debouncedQuery = useDebounce(query, 300);

  const { doctors, isFetching, isError, avatars, avatarErrors } = useDoctors({
    date: "",
    specialty: null,
    availableOnly: false,
    searchTerm: "",
  });

  const doctorResults = useMemo(() => {
    return searchDoctors(query, doctors);
  }, [debouncedQuery, doctors]);

  const clinicResults = useMemo(() => {
    return searchClinics(query, clinics);
  }, [debouncedQuery, clinics]);

  const handleBookAppointment = (doctor: DoctorDto) => {
    let doctorId = String(doctor.doctorId);

    if (!doctorId) return;

    scrollToTop();

    sessionStorage.clear();

    const params = new URLSearchParams({
      type: "doctor",
      doctorId,
      step: "chooseClinic",
    });

    setTimeout(() => {
      navigate(`/dat-hen?${params.toString()}`);
    }, 0);
  };

  const handleViewDetails = (doctor: DoctorDto) => {
    if (!doctor.doctorId) return;

    const params = new URLSearchParams({
      doctorId: doctor.doctorId,
    });

    navigate(`/profile?${params.toString()}`);
  };

  const handleClinicAction = (
    clinic: ClinicScheduleStatusDto,
    type: ModalType
  ) => {
    setSelectedClinic(clinic ?? null);
    setModalType(type);
    setIsModalOpen(true);
  };

  const onModalTrigger = (open: boolean) => {
    if (!open) {
      setSelectedClinic(null);
    }
    setIsModalOpen(open);
  };

  const fetchClinicsData = async () => {
    const client = new SpecialtiesClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });

    client
      .getClinics(1, true)
      .then((data) => {
        setClinics(data);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.error("Máy chủ mất quá lâu để phản hồi");
        } else {
          console.error("Có lỗi xảy ra khi kết nối danh sách phòng khám.");
        }
      });
  };

  useEffect(() => {
    searchRef.current?.focus();

    fetchClinicsData();

    document.body.style.backgroundColor =
      "rgb(243 244 246 / var(--tw-bg-opacity, 1))";

    return () => {
      document.body.style.backgroundColor = "hsl(144deg 48.39% 93.92%)";
    };
  }, []);

  useEffect(() => {
    document.title = t("timkiemdatkham");
  }, [t]);

  return (
    <div className="flex flex-col h-full">
      {/* Top search bar */}
      <div className="p-4 w-full bg-white ">
        <div
          className={`flex items-center space-x-4 w-full
            ${!isMobile && "max-w-6xl mx-auto"}`}
        >
          {isMobile && (
            <button
              className="flex items-center"
              onClick={() => {
                navigate(-1);
              }}
            >
              <ArrowLeft className="w-6 h-6 text-green-600" />
            </button>
          )}

          <div className=" w-full flex justify-center">
            <input
              ref={searchRef}
              placeholder={t("timkiembacsi")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="mobile-input-base border-green-600 hover:border-green-700 focus:border-green-800 focus:ring-2 focus:ring-green-200 w-full max-w-md"
            />
          </div>
        </div>
      </div>

      <div className="flex border-b shadow-lg bg-white">
        <div className={`w-full flex ${!isMobile && "max-w-6xl mx-auto"}`}>
          <button
            onClick={() => {
              setActiveTab("doctor");
            }}
            className={`text-base border-b-2 flex-1 py-3 px-6 text-center font-medium transition-colors duration-200 ${
              activeTab === "doctor"
                ? "border-green-600 text-green-600"
                : "text-gray-600 hover:text-green-600 border-transparent"
            }`}
          >
            {t("bacsi")}
          </button>
          <button
            onClick={() => {
              setActiveTab("clinic");
            }}
            className={`text-base border-b-2 flex-1 py-3 px-6 text-center font-medium transition-colors duration-200 ${
              activeTab === "clinic"
                ? "border-green-600 text-green-600"
                : "text-gray-600 hover:text-green-600 border-transparent "
            }`}
          >
            {t("phongkham")}
          </button>
        </div>
      </div>

      <div className={`h-full ${!isMobile && "max-w-6xl w-full mx-auto"}`}>
        {activeTab === "doctor" &&
          (isFetching ? (
            <LoadingIndicator />
          ) : isError ? (
            <div className="p-4 flex justify-center pb-[15px]">
              <ErrorRetry />
            </div>
          ) : !!doctorResults.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              {doctorResults.map((doctor) => (
                <DoctorCard
                  key={doctor.doctorId}
                  doctor={doctor}
                  onBookAppointment={handleBookAppointment}
                  onViewDetails={handleViewDetails}
                  hasAvatar={avatars?.[String(doctor.doctorId ?? "")]}
                  avatarError={avatarErrors[String(doctor.doctorId)]}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="rounded-full flex items-center justify-center mx-auto mb-4">
                <img
                  className="w-[120px] h-[120px]"
                  src="/images/non-schedule.svg"
                />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {t("khongtimthaybacsi2")}
              </h3>
            </div>
          ))}

        {activeTab === "clinic" &&
          (isFetching ? (
            <LoadingIndicator />
          ) : isError ? (
            <div className="p-4 flex justify-center pb-[15px]">
              <ErrorRetry />
            </div>
          ) : !!clinicResults.length ? (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 pb-[15px]">
              {clinicResults.map((clinic) => (
                <HorizontalCard
                  isMobile={isMobile ?? false}
                  key={clinic.clinicId}
                  clinic={clinic}
                  onSubmitClick={(clinic) =>
                    handleClinicAction(clinic, "submit")
                  }
                  onContainerClick={(clinic) =>
                    handleClinicAction(clinic, "detail")
                  }
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="rounded-full flex items-center justify-center mx-auto mb-4">
                <img
                  className="w-[120px] h-[120px]"
                  src="/images/non-schedule.svg"
                />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {t("khongtimthayphongkham")}
              </h3>
            </div>
          ))}

        <ClinicDetailModal
          type={modalType}
          clinic={selectedClinic}
          isOpen={isModalOpen}
          onTrigger={onModalTrigger}
          onRegister={(clinic) => handleClinicAction(clinic, "submit")}
        />

        <NonScheduleDialog
          open={isEmptySlot}
          onOpenChange={setIsEmptySlot}
          message={emptyDataMessage}
        />
      </div>
    </div>
  );
}
